/**
 * Борлуулалт амжилттай — сагсны ОРОНД гарна (dark glass, 2026-10-05).
 *
 * Цагираг зурагдаж, чек тэмдэг гарч, доор нь захиалгын хураангуй:
 * дүн · төлбөрийн хэлбэр · И-баримтын НӨАТ · код. Утга бүгд СЕРВЕРИЙН
 * хариунаас (`POST /orders`) — маягт аль хэдийн цэвэрлэгдсэн.
 *
 * ⚠️ Дизайны «И-баримт илгээсэн» гэсэн бичгийг ХУУЛААГҮЙ — систем
 *    и-баримтыг татварын системд илгээдэггүй (CLAUDE.md «Кассын нэмэгдэл»).
 *    Оронд нь бодогдсон НӨАТ-ын дүн.
 * ⚠️ Зээлийн аппаар (хэсэгчлэн ч) төлсөн бол баримт ӨӨРӨӨ хэвлэгдэнэ
 *    (2026-09-28) — `ReceiptPrint autoPrint`.
 */

import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatMnt } from '@boots/domain';
import { PAYMENT_LABEL } from '../lib/types';
import { Button } from './ui';
import { ReceiptPrint } from './receipt-print';

export interface OrderCreated {
  id: string;
  code: string;
  totalMnt: string;
  vatMnt: string;
  payments: { method: string }[];
  _count: { lines: number };
}

const CHIP = 'rounded-xl px-3.5 py-2 text-h3 font-semibold';

export function SaleSuccess({ order, onNew }: { order: OrderCreated; onNew: () => void }) {
  const navigate = useNavigate();
  const methods = [...new Set(order.payments.map((p) => PAYMENT_LABEL[p.method] ?? p.method))];
  const vat = Number(order.vatMnt) > 0;
  const step = (i: number) => ({ '--i': i } as CSSProperties);

  return (
    <div role="status" className="flex flex-1 flex-col items-center justify-center py-14 text-center">
      <div className="pop-in relative size-37.5">
        <span
          aria-hidden
          className="absolute -inset-10 rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-accent)_35%,transparent),transparent_70%)]"
        />
        <svg viewBox="0 0 150 150" className="absolute inset-0 -rotate-90" aria-hidden>
          <circle
            cx="75" cy="75" r="68" pathLength={1}
            fill="var(--color-accent-100)" stroke="var(--color-accent)" strokeWidth="5" strokeLinecap="round"
            className="draw" style={{ '--delay': '100ms' } as CSSProperties}
          />
        </svg>
        <svg viewBox="0 0 150 150" className="absolute inset-0" aria-hidden>
          <path
            d="M48 77 L67 96 L104 57" pathLength={1}
            fill="none" stroke="var(--color-ink)" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round"
            className="draw" style={{ '--delay': '550ms' } as CSSProperties}
          />
        </svg>
      </div>

      {/* Unbounded — «Борлуулалт амжилттай»-д Ө/Ү байхгүй */}
      <p style={step(3)} className="rise mt-8 font-display text-num-md font-semibold">Борлуулалт амжилттай</p>

      <div style={step(4)} className="rise mt-4 flex flex-wrap justify-center gap-2.5">
        <span className={`num bg-accent-100 text-accent-700 ${CHIP}`}>{formatMnt(order.totalMnt)}</span>
        {methods.map((m) => (
          <span key={m} className={`bg-neutral-bg text-ink-2 ${CHIP}`}>{m}</span>
        ))}
        {vat ? (
          <span className={`num bg-neutral-bg text-ink-2 ${CHIP}`}>И-баримт · НӨАТ {formatMnt(order.vatMnt)}</span>
        ) : null}
        <span className={`num bg-neutral-bg text-ink-3 ${CHIP}`}>{order.code}</span>
      </div>

      <div style={step(5)} className="rise mt-7 flex flex-wrap justify-center gap-2">
        <ReceiptPrint
          key={order.id}
          orderId={order.id}
          autoPrint={order.payments.some((p) => p.method === 'CREDIT_APP')}
        />
        <Button variant="ghost" onClick={() => navigate(`/orders?open=${order.id}`)}>Захиалга харах</Button>
        <Button onClick={onNew}>Шинэ борлуулалт</Button>
      </div>
    </div>
  );
}
